import type { SkeletonProps, TypographyProps } from '@mui/material';
import { Skeleton, Typography } from '@mui/material';
import useStyles from './SkeletonLoader.styles';

/**
 * SkeletonLoader provides a consistent loading experience across BPS.
 *
 * This component wraps MUI's Skeleton component with a controlled API to enforce
 * styling limitations. Previously, direct usage of Material UI's Skeleton
 * exposed additional options and variants, leading to inconsistent loading
 * states throughout the application.
 *
 * The component enforces specific variants (`text`, `rounded`, `circular`) that map to
 * MUI's Skeleton variants and applies consistent styling with a customized border-radius.
 * Additional options can be added as future needs arise or design system requirements evolve.
 *
 * @see https://mui.com/material-ui/react-skeleton/
 */

/**
 * Supported skeleton variants:
 * - `text`: For text content (e.g., headings, paragraphs, labels)
 * - `rounded`: For UI elements (e.g., buttons, cards, form inputs, large block elements/containers)
 * - `circular`: For circular elements (e.g., avatars, profile images, icons)
 */
type SkeletonLoaderVariant = 'text' | 'rounded' | 'circular';

export interface SkeletonLoaderProps {
  /**
   * The variant of the skeleton. Defaults to 'rounded'.
   * See SkeletonLoaderVariant type for usage guidance.
   */
  variant?: SkeletonLoaderVariant;
  /**
   * Width of the skeleton
   */
  width?: SkeletonProps['width'];
  /**
   * Height of the skeleton.
   * - For 'text' variant: Converted to fontSize (MUI ignores direct height for text).
   *   If both height and typographyVariant are provided, height takes precedence
   *   (applied via fontSize), consistent with MUI's sx behavior.
   * - For 'rounded' and 'circular' variants: Applied directly as height.
   */
  height?: SkeletonProps['height'];
  /**
   * Typography variant for 'text' variant. Wraps the skeleton in Typography
   * to inherit the variant's line-height and font properties.
   */
  typographyVariant?: TypographyProps['variant'];
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

const MUI_SKELETON_VARIANTS: Record<
  SkeletonLoaderVariant,
  SkeletonProps['variant']
> = {
  text: 'text',
  rounded: 'rounded',
  circular: 'circular',
};

const SkeletonLoader = ({
  variant = 'rounded',
  width,
  height,
  typographyVariant,
  testId,
}: SkeletonLoaderProps) => {
  const { classes } = useStyles();
  const muiVariant = MUI_SKELETON_VARIANTS[variant];

  /*
   * Implementation varies based on variant and props:
   * - Text with Typography: Wraps skeleton to inherit variant's line-height/font properties
   * - Text standalone: Height becomes fontSize; MUI scales line-height by 0.6, we override to 0.70 for more accurate reflection of the actual text font size
   * - Rounded/Circular: Standard skeleton with direct height/width control
   */

  if (variant === 'text') {
    const textSkeleton = (
      <Skeleton
        variant={muiVariant}
        width={width}
        {...(height && { sx: { fontSize: height } })}
        className={`${classes.text} ${classes.borderRadius}`}
        data-testid={testId}
      />
    );

    return typographyVariant ? (
      <Typography variant={typographyVariant}>{textSkeleton}</Typography>
    ) : (
      textSkeleton
    );
  }

  return (
    <Skeleton
      variant={muiVariant}
      width={width}
      height={height}
      className={variant === 'circular' ? '' : classes.borderRadius}
      data-testid={testId}
    />
  );
};

export default SkeletonLoader;
